/**
 * Custom StackedHorizontalBarChartComponent - Override Component
 * Supports 3 stacked partitions for downtime visualization
 */
import { Component, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntil, switchMap } from 'rxjs/operators';
import * as echarts from 'echarts';
import { StackedHorizontalBarChartComponent as ParentStackedHorizontalBarChartComponent } from '@shared-lib/chart-components/stacked-horizontal-bar-chart/stacked-horizontal-bar-chart.component';
import { RepowerDowntimeStatusTableComponent } from '../table/table.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

type AllSeriesDataType = {
  cumminsValues: number[];
  nonCumminsValues: number[];
  availableValues: number[];
  totalValues?: number[];
  cumminsLabel: string;
  nonCumminsLabel: string;
  cumminsColor: string;
  nonCumminsColor: string;
};

@Component({
  selector: 'app-stacked-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, RepowerDowntimeStatusTableComponent],
  templateUrl: './stacked-horizontal-bar-chart.component.html',
  styleUrls: ['./stacked-horizontal-bar-chart.component.scss']
})
export class StackedHorizontalBarChartComponent extends ParentStackedHorizontalBarChartComponent {

  protected override allSeriesData: AllSeriesDataType | null = null;
  private _payloadFilterApplied = false;
  monthOptions: Array<{ label: string; value: number }> = [];

  years: number[] = [];
  startMonth = 1;
  endMonth = 12;
  startYear = new Date().getFullYear();
  endYear = new Date().getFullYear();
  private dateFilterOptionsLoaded = false;
  private greenCalloutGraphicIds: string[] = [];

  constructor(
    protected override configService: ConfigService,
    protected override globalFilterService: GlobalFilterService,
    private cdr: ChangeDetectorRef
  ) {
    super(configService, globalFilterService);
  }

  public popupType: 'hours' | 'percent' | null = null;
  public popupX = 0;
  public popupY = 0;

  toggleInfoPopup(nativeEvent: MouseEvent | null, type: 'hours' | 'percent'): void {
    if (nativeEvent) nativeEvent.stopPropagation();

    if (this.popupType === type) {
      this.popupType = null;
    } else {
      this.popupType = type;

      // Position popup above the corresponding red underline based on the type
      if (this.chartContainer?.nativeElement) {
        const chartRect = this.chartContainer.nativeElement.getBoundingClientRect();
        this.popupX = chartRect.left + -1;
        
        if (type === 'hours') {
          // Position above the first red underline (Engine Uptime Hours)
          this.popupY = chartRect.top + 135;
        } else if (type === 'percent') {
          // Position above the second red underline (Engine Uptime %)
          this.popupY = chartRect.top + 275;
        }
      }

      setTimeout(() => {
        const closeHandler = (e: MouseEvent) => {
          const popup = document.querySelector('.info-popup-fixed');
          if (popup && !popup.contains(e.target as Node)) {
            this.popupType = null;
            this.cdr.detectChanges();
            document.removeEventListener('click', closeHandler);
          }
        };
        document.addEventListener('click', closeHandler);
      }, 0);
    }
    this.cdr.detectChanges();
  }

  override ngAfterViewInit(): void {
    this.initializeRangeDefaults();
    this.fetchDateFilterOptions();
    this.chartInstance = echarts.init(this.chartContainer.nativeElement);
    // If data arrived before the chart instance was created, render now
    if (this.chartData) {
      this.initChart(this.chartData);
    }

    // Always listen to global filter changes so month/year range can trigger backend refresh.
    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => this.loadAndRenderChart(filters))
      )
      .subscribe({
        next: (res: any) => {
          const chartData = res?.data || res;
          if (chartData) this.initChart(chartData);
        },
        error: () => { /* ignore for now */ }
      });

    // Fetch initial data once using current filters (if any)
    // When skipDefaultPayload is true, pass undefined filters to use JSON config
    try {
      const initialFilters =
        ((this.globalFilterService as any).getCurrentFilters
          ? (this.globalFilterService as any).getCurrentFilters()
          : undefined);
      this.loadAndRenderChart(initialFilters).pipe(takeUntil(this.unSubscribe$)).subscribe({
        next: (res: any) => {
          const chartData = res?.data || res;
          if (chartData) this.initChart(chartData);
        },
        error: () => { /* ignore */ }
      });
    } catch (e) {
      // ignore if getCurrentFilters not available
    }
  }

  protected override loadAndRenderChart(_filters?: any): any {
    const dataForRequest = JSON.parse(JSON.stringify(this.data || {}));

    const requestFilter =
      dataForRequest?.customPayload?.requestData?.filter ||
      dataForRequest?.requestData?.filter ||
      {};

    requestFilter['Startyear'] = String(this.startYear);
    requestFilter['EndYear'] = String(this.endYear);
    requestFilter['Startmonth'] = this.getMonthFullLabel(this.startMonth);
    requestFilter['Endmonth'] = this.getMonthFullLabel(this.endMonth);

    const navState = (window?.history?.state || {}) as any;
    const selectedEngineModelKey =
      navState?.selectedEngineModelKey || 
      navState?.engine_model_key ||
      navState?.selectedEngine ||
      navState?.engine_model;

    // For this widget API, keep only clean_engine_model in filter payload.
    if (requestFilter['engine_model_key']) {
      delete requestFilter['engine_model_key'];
    }

    const configuredCleanEngineModel = String(requestFilter['clean_engine_model'] || '')
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (configuredCleanEngineModel) {
      requestFilter['clean_engine_model'] = configuredCleanEngineModel;
    }
    if (!configuredCleanEngineModel && selectedEngineModelKey) {
      requestFilter['clean_engine_model'] = String(selectedEngineModelKey)
        .replace(/,/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    if (dataForRequest?.customPayload?.requestData?.filter) {
      dataForRequest.customPayload.requestData.filter = requestFilter;
    } else {
      if (!dataForRequest.requestData) dataForRequest.requestData = {};
      dataForRequest.requestData.filter = requestFilter;
    }

    return this.configService.getConfig(
      dataForRequest.apiUrl,
      dataForRequest,
      { useQueryParams: this.data?.useQueryParams || false },
      dataForRequest.method
    );
  }

  protected override renderChartWithVisibleData(cumminsLabel: string, nonCumminsLabel: string, cumminsColor: string, nonCumminsColor: string): void {
    if (!this.allSeriesData) return;
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 767;

    const displayCategories = (this.visibleCategories || []).slice().reverse();
    const series0 = (this.visibleSeries[0]?.data || []).slice();
    const series1 = (this.visibleSeries[1]?.data || []).slice();
    const series0Reversed = series0.slice().reverse();
    const series1Reversed = series1.slice().reverse();
    const series2 = (this.visibleSeries[2]?.data || []).slice();
    const series2Reversed = series2.slice().reverse();
    const widgetChartConfig = (this.data && this.data.chartConfig) || {};
    const configuredBarWidth = Number(widgetChartConfig?.barWidth);
    const dynamicBarWidthEnabled = widgetChartConfig?.dynamicBarWidth !== false;
    const barWidthMin = Number(widgetChartConfig?.barWidthMin ?? (isSmallScreen ? 10 : 14));
    const barWidthMax = Number(widgetChartConfig?.barWidthMax ?? (isSmallScreen ? 24 : 40));

    const resolvedChartHeight = this.getChartHeight();
    const chartHeightPxMatch = String(resolvedChartHeight).trim().match(/^(\d+(?:\.\d+)?)px$/);
    const fallbackChartHeight = isSmallScreen ? 220 : 300;
    const chartHeightPx = chartHeightPxMatch ? Number(chartHeightPxMatch[1]) : fallbackChartHeight;
    const visibleRowCount = Math.max(displayCategories.length, 1);
    const estimatedGridReserved = isSmallScreen ? 96 : 120;
    const estimatedPlotHeight = Math.max(chartHeightPx - estimatedGridReserved, 80);
    const computedBarWidth = Math.floor(estimatedPlotHeight / (visibleRowCount * 2.2));

    let thinBarWidth = Number.isFinite(configuredBarWidth) && configuredBarWidth > 0
      ? configuredBarWidth
      : computedBarWidth;

    if (dynamicBarWidthEnabled) {
      const minBound = Number.isFinite(barWidthMin) && barWidthMin > 0 ? barWidthMin : (isSmallScreen ? 10 : 14);
      const maxBound = Number.isFinite(barWidthMax) && barWidthMax >= minBound ? barWidthMax : (isSmallScreen ? 24 : 40);
      thinBarWidth = Math.max(minBound, Math.min(maxBound, thinBarWidth));
    }
    const configuredVisualScale = Number(widgetChartConfig?.percentageVisualScale);
    const configuredScaleMin = Number(widgetChartConfig?.percentageVisualScaleMin ?? 50);
    const configuredScaleMax = Number(widgetChartConfig?.percentageVisualScaleMax ?? 5000);
    const scaleMin = Number.isFinite(configuredScaleMin) && configuredScaleMin > 0 ? configuredScaleMin : 50;
    const scaleMax = Number.isFinite(configuredScaleMax) && configuredScaleMax > scaleMin ? configuredScaleMax : 5000;
    const isPercentageRow = (idx: number) => (displayCategories[idx] || '').includes('%');

    const rowRawTotals = series0Reversed.map((_: any, idx: number) => {
      return Number(series0Reversed[idx] || 0) + Number(series1Reversed[idx] || 0) + Number(series2Reversed[idx] || 0);
    });

    const nonPercentageTotals = rowRawTotals.filter((total: number, idx: number) => !isPercentageRow(idx) && total > 0);
    const percentageTotals = rowRawTotals.filter((total: number, idx: number) => isPercentageRow(idx) && total > 0);

    let percentageVisualScale = Number.isFinite(configuredVisualScale) && configuredVisualScale > 0
      ? configuredVisualScale
      : 1;

    if (!(Number.isFinite(configuredVisualScale) && configuredVisualScale > 0) && percentageTotals.length > 0 && nonPercentageTotals.length > 0) {
      const maxNonPercentageTotal = Math.max(...nonPercentageTotals);
      const maxPercentageTotal = Math.max(...percentageTotals);
      const targetWidth = maxNonPercentageTotal * 0.9;
      const computedScale = maxPercentageTotal > 0 ? targetWidth / maxPercentageTotal : 1;
      percentageVisualScale = Math.min(scaleMax, Math.max(scaleMin, computedScale));
    }

    const series0Display = series0Reversed.map((value: any, idx: number) => {
      const raw = Number(value || 0);
      return isPercentageRow(idx) ? raw * percentageVisualScale : raw;
    });
    const series1Display = series1Reversed.map((value: any, idx: number) => {
      const raw = Number(value || 0);
      return isPercentageRow(idx) ? raw * percentageVisualScale : raw;
    });
    const series2Display = series2Reversed.map((value: any, idx: number) => {
      const raw = Number(value || 0);
      return isPercentageRow(idx) ? raw * percentageVisualScale : raw;
    });

    const configuredTotals = (this.allSeriesData?.totalValues || []).slice().reverse();
    const totalsForLabel = series0Display.map((v: any, i: number) => {
      // Keep percentage row end label as fixed reference total.
      if (isPercentageRow(i)) return 100;

      const configuredTotal = Number(configuredTotals[i]);
      if (!isNaN(configuredTotal)) return configuredTotal;
      return Number(series0Reversed[i] || 0) + Number(series1Reversed[i] || 0) + Number(series2Reversed[i] || 0);
    });

    const totalsForAxis = series0Display.map((v: any, i: number) => {
      const a = Number(v || 0);
      const b = Number(series1Display[i] || 0);
      const c = Number(series2Display[i] || 0);
      return a + b + c;
    });

    const xMin = 0;
    const xMax = totalsForAxis.length ? Math.max(...totalsForAxis) : 0;
    const axisMax = xMax ? xMax * 1.12 : 0;

    const gridConfig = widgetChartConfig.grid || { left: '3%', right: '80px', top: '10%', bottom: '3%', containLabel: true };

    // Set bar colors: green, orange, dark gray
    const colors = ['#7A942E', '#FFA500', '#444444'];

    const series: any[] = [
      {
        name: cumminsLabel,
        type: 'bar',
        stack: 'total',
        barWidth: thinBarWidth,
        label: {
          show: false,
          position: 'right',
          distance: 12,
          formatter: (params: any) => {
            const val = Number(params?.value || 0);
            return this.formatCustomLabel(val);
          },
          color: '#000000',
          fontSize: 11,
          fontWeight: 'bold'
        },
        data: series0Display,
        itemStyle: {
          color: colors[0],
          borderWidth: 0
        }
      },
      {
        name: nonCumminsLabel,
        type: 'bar',
        stack: 'total',
        barWidth: thinBarWidth,
        label: {
          show: false,
          position: 'right',
          distance: 12,
          formatter: (params: any) => {
            const val = Number(params?.value || 0);
            return this.formatCustomLabel(val);
          },
          color: '#000000',
          fontSize: 11,
          fontWeight: 'bold'
        },
        data: series1Display,
        itemStyle: {
          color: colors[1],
          borderWidth: 0
        }
      },
      {
        name: 'Available',
        type: 'bar',
        stack: 'total',
        barWidth: thinBarWidth,
        label: {
          show: true,
          position: 'right',
          distance: 10,
          formatter: (params: any) => {
            const idx = params.dataIndex;
            const total = Number(totalsForLabel[idx] || 0);
            const category = displayCategories[idx] || '';
            return category.includes('%') ? '100%' : this.formatCustomLabel(total);
          },
          color: '#000000',
          fontSize: 12,
          fontWeight: 'bold'
        },
        data: series2Display.map((val: any) => Number(val || 0)),
        itemStyle: {
          color: colors[2],
          borderWidth: 0
        }
      }
    ];

    const option = {
      grid: isSmallScreen
        ? { left: '34%', right: '10%', top: '16%', bottom: '8%', containLabel: false }
        : gridConfig,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const list = Array.isArray(params) ? params : [params];
          const idx = Number(list?.[0]?.dataIndex ?? 0);
          const category = (displayCategories[idx] || '').replace('Engine Uptime %', 'Engine Downtime %');
          const unplannedActual = Number(series0Reversed[idx] || 0);
          const plannedActual = Number(series1Reversed[idx] || 0);
          const availableActual = Number(series2Reversed[idx] || 0);
          const totalActual = Number(totalsForLabel[idx] || (unplannedActual + plannedActual + availableActual));
          return `${category}<br/>Unplanned: ${this.formatCustomLabel(unplannedActual)}<br/>Planned: ${this.formatCustomLabel(plannedActual)}<br/>Available: ${this.formatCustomLabel(availableActual)}`;
        }
      },
      legend: {
        data: [cumminsLabel, nonCumminsLabel, 'Available'],
        type: isSmallScreen ? 'scroll' : 'plain',
        top: isSmallScreen ? 0 : -4,
        left: 'center',
        itemWidth: isSmallScreen ? 16 : 24,
        itemHeight: isSmallScreen ? 10 : 14,
        itemGap: isSmallScreen ? 10 : 16,
        textStyle: {
          fontSize: isSmallScreen ? 11 : 14
        }
      },
      xAxis: {
        type: 'value',
        min: xMin,
        max: axisMax,
        axisLabel: {
          fontSize: isSmallScreen ? 10 : 12,
          formatter: (value: any) => {
            const eps = Math.max(Math.abs(axisMax) * 1e-6, 1e-6);
            if (Math.abs(value - xMin) <= eps || Math.abs(value - axisMax) <= eps) return this.formatCustomLabel(value);
            return '';
          }
        }
      },
      yAxis: {
        type: 'category',
        data: displayCategories,
        triggerEvent: true,
        axisLabel: {
          align: 'right',
          margin: 10,
          fontSize: isSmallScreen ? 11 : 14,
          formatter: (value: any) => {
            if (value === 'Engine Uptime Hours' || value === 'Engine Uptime %') {
              return `${value} {btn|i}`;
            }
            return value;
          },
          rich: {
            btn: {
              backgroundColor: '#f1f5f9',
              borderColor: '#cbd5e1',
              borderWidth: 2,
              borderRadius: 10,
              width: 18,
              height: 18,
              lineHeight: 20,
              align: 'center',
              verticalAlign: 'middle',
              color: '#64748b',
              fontWeight: '900',
              fontSize: 12,
              padding: [0, 0, 2, 0]
            }
          }
        }
      },
      graphic: [],
      series: series
    };

    this.chartInstance.setOption(option);
    
    // Unbind previous listener to avoid duplicates on re-render
    this.chartInstance.off('click');
    this.chartInstance.on('click', (params: any) => {
      if (params.componentType === 'yAxis') {
        const val = String(params.value || '');
        // Get the native browser MouseEvent from the ECharts event
        const nativeEvent: MouseEvent | null = params.event?.event ?? null;
        if (val.includes('Engine Uptime Hours')) {
          this.toggleInfoPopup(nativeEvent, 'hours');
        } else if (val.includes('Engine Uptime %')) {
          this.toggleInfoPopup(nativeEvent, 'percent');
        }
      }
    });
    this.renderAllSegmentCallouts(
      displayCategories,
      [
        series0Display.map((v: any) => Number(v || 0)),
        series1Display.map((v: any) => Number(v || 0)),
        series2Display.map((v: any) => Number(v || 0))
      ],
      [
        series0Reversed.map((v: any) => Number(v || 0)),
        series1Reversed.map((v: any) => Number(v || 0)),
        series2Reversed.map((v: any) => Number(v || 0))
      ],
      axisMax,
      widgetChartConfig
    );
  }

  private renderAllSegmentCallouts(
    displayCategories: string[],
    allSeriesDisplay: number[][],
    allSeriesRaw: number[][],
    axisMax: number,
    widgetChartConfig: any
  ): void {
    if (!this.chartInstance) return;

    const calloutConfig = widgetChartConfig?.greenCallout;
    if (!calloutConfig || calloutConfig.enabled !== true) {
      this.updateGreenCalloutGraphics([]);
      return;
    }

    const defaultCalloutColor = typeof calloutConfig?.color === 'string'
      ? String(calloutConfig.color).trim()
      : '#000000';

    const lineWidth = this.readRequiredNumber(calloutConfig, 'lineWidth') || 2;
    const horizontalBack = this.readRequiredNumber(calloutConfig, 'horizontalBack') || 80;
    const baseVerticalDrop = this.readRequiredNumber(calloutConfig, 'verticalDrop') || 25;
    const labelOffsetX = this.readRequiredNumber(calloutConfig, 'labelOffsetX') || 10;
    const labelFontSize = this.readRequiredNumber(calloutConfig, 'labelFontSize') || 12;
    const labelPercentDecimals = this.readRequiredNumber(calloutConfig, 'labelPercentDecimals') || 2;
    const lineZ = this.readRequiredNumber(calloutConfig, 'lineZ') || 40;
    const labelZ = this.readRequiredNumber(calloutConfig, 'labelZ') || 41;
    const labelFontWeight = this.readRequiredNumber(calloutConfig, 'labelFontWeight') || 700;
    const labelFontFamily = typeof calloutConfig?.labelFontFamily === 'string'
      ? String(calloutConfig.labelFontFamily).trim()
      : 'sans-serif';

    // New keys for uniform sizing
    const lineLength = this.readRequiredNumber(calloutConfig, 'lineLength') ?? horizontalBack;
    const verticalTickHeight = this.readRequiredNumber(calloutConfig, 'verticalTickHeight');
    const verticalStaggerFactor = this.readRequiredNumber(calloutConfig, 'verticalStagger') ?? 22;

    const graphicElements: any[] = [];
    const segmentColors = ['#7A942E', '#FFA500', '#444444']; // Unplanned, Planned, Available

    displayCategories.forEach((category, idx) => {
      let cumulativeDisplay = 0;
      const placedLabelsInRow: Array<{ x1: number, x2: number, y: number }> = [];

      [0, 1, 2].forEach((sIdx) => {
        const rawValue = Number(allSeriesRaw[sIdx][idx] || 0);
        const visibleValue = Number(allSeriesDisplay[sIdx][idx] || 0);
        
        const previousEndDisplay = cumulativeDisplay;
        cumulativeDisplay += visibleValue;
        
        if (!(rawValue <= 0 && visibleValue <= 0)) {
          const segmentCenterDisplay = previousEndDisplay + (visibleValue / 2);

          const segmentCenterPixel = this.chartInstance.convertToPixel(
            { xAxisIndex: 0, yAxisIndex: 0 },
            [segmentCenterDisplay, category]
          ) as number[];
          
          if (Array.isArray(segmentCenterPixel) && Number.isFinite(segmentCenterPixel[0]) && Number.isFinite(segmentCenterPixel[1])) {
            const barCentreY = segmentCenterPixel[1];
            const barWidthPx = Number(widgetChartConfig?.barWidth) || 30;
            const barBottomY = barCentreY + barWidthPx / 2;
            
            const labelText = String(category).includes('%')
              ? `${rawValue.toFixed(labelPercentDecimals)}%`
              : this.formatCustomLabel(rawValue);
            
            // Estimate label width for collision detection (approx 8px per char)
            const estimatedLabelWidth = labelText.length * 8;
            
            const greenEndX = segmentCenterPixel[0];
            const lineEndX = greenEndX + lineLength;
            const labelStartX = lineEndX + labelOffsetX;
            
            // Horizontal range occupied by this callout (line + label)
            const x1 = Math.min(greenEndX, lineEndX, labelStartX);
            const x2 = Math.max(greenEndX, lineEndX, labelStartX + estimatedLabelWidth);
            
            // Dynamic staggering: find a Y level that doesn't collide
            let arrowY = barCentreY + baseVerticalDrop;
            const minVerticalGap = (verticalStaggerFactor > 0 ? verticalStaggerFactor : 22);
            
            let collision = true;
            while (collision) {
              collision = false;
              for (const placed of placedLabelsInRow) {
                // Check if Y levels are too close
                if (Math.abs(arrowY - placed.y) < (minVerticalGap * 0.8)) {
                  // Check if X ranges overlap (with small padding)
                  if (x1 < placed.x2 + 10 && x2 > placed.x1 - 10) {
                    collision = true;
                    arrowY += minVerticalGap;
                    break;
                  }
                }
              }
            }

            placedLabelsInRow.push({ x1, x2, y: arrowY });
            const segmentColor = segmentColors[sIdx] || defaultCalloutColor;
            const baseId = `segment-callout-${idx}-${sIdx}`;

            // Vertical connecting tick
            graphicElements.push({
              id: `${baseId}-tick`,
              type: 'line',
              silent: true,
              z: lineZ,
              shape: { 
                x1: greenEndX, 
                y1: verticalTickHeight !== null ? (arrowY - verticalTickHeight) : barBottomY, 
                x2: greenEndX, 
                y2: arrowY 
              },
              style: {
                stroke: segmentColor,
                lineWidth: lineWidth,
                lineCap: 'round'
              }
            });

            // Horizontal line
            graphicElements.push({
              id: `${baseId}-line`,
              type: 'line',
              silent: true,
              z: lineZ,
              shape: { x1: greenEndX, y1: arrowY, x2: lineEndX, y2: arrowY },
              style: {
                stroke: segmentColor,
                lineWidth: lineWidth,
                lineCap: 'round'
              }
            });

            // Arrowhead triangle pointing right
            const ahs = Number(calloutConfig?.arrowHeadSize) || 8;
            const abhw = Number(calloutConfig?.arrowBaseHalfWidthShare) || 0.45;
            const halfWidth = ahs * abhw;
            const tipX = lineEndX;
            const baseX = tipX - ahs;

            graphicElements.push({
              id: `${baseId}-arrow`,
              type: 'polygon',
              silent: true,
              z: labelZ,
              shape: {
                points: [
                  [tipX, arrowY],
                  [baseX, arrowY - halfWidth],
                  [baseX, arrowY + halfWidth]
                ]
              },
              style: {
                fill: segmentColor,
                stroke: segmentColor
              }
            });

            // Value label
            graphicElements.push({
              id: `${baseId}-label`,
              type: 'text',
              silent: true,
              z: labelZ,
              style: {
                x: labelStartX,
                y: arrowY,
                text: labelText,
                fill: segmentColor,
                font: `${labelFontWeight} ${labelFontSize}px ${labelFontFamily}`,
                textAlign: 'left',
                textVerticalAlign: 'middle'
              }
            });
          }
        }
      });
    });

    this.updateGreenCalloutGraphics(graphicElements);
  }


  private readRequiredNumber(config: any, key: string): number | null {
    const value = Number(config?.[key]);
    return Number.isFinite(value) ? value : null;
  }

  private updateGreenCalloutGraphics(nextElements: any[]): void {
    if (!this.chartInstance) return;
    const nextIds = nextElements
      .map((element: any) => String(element?.id || '').trim())
      .filter((id: string) => id !== '');

    const removeElements = this.greenCalloutGraphicIds
      .filter((id) => !nextIds.includes(id))
      .map((id) => ({ id, $action: 'remove' as const }));

    this.chartInstance.setOption({
      graphic: {
        elements: [...removeElements, ...nextElements]
      }
    });

    this.greenCalloutGraphicIds = nextIds;
  }

  private formatCustomLabel(value: any): string {
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  protected override resolveWidgetHeight(): any {
    return super.resolveWidgetHeight();
  }

  getChartHeight(): string {
    const chartHeight = this.data?.chartConfig?.chartHeight || this.data?.chartConfig?.plotHeight;
    const resolvedHeight = chartHeight ? String(chartHeight) : '220px';
    const pxMatch = resolvedHeight.trim().match(/^(\d+(?:\.\d+)?)px$/);
    if (pxMatch) {
      const base = Number(pxMatch[1]);
      return `${base + 40}px`;
    }
    return resolvedHeight;
  }

  override getContainerHeight(): any {
    return super.getContainerHeight();
  }

  override initChart(data: any): void {
    if (!data) return;

    // Map filter defaults from payload directly to dropdowns (runs once)
    const filter = this.data?.customPayload?.requestData?.filter || this.data?.requestData?.filter;
    if (filter && !this._payloadFilterApplied) {
      this._payloadFilterApplied = true;
      const mMap: Record<string, number> = {
        january: 1, jan: 1, february: 2, feb: 2, march: 3, mar: 3,
        april: 4, apr: 4, may: 5, june: 6, jun: 6, july: 7, jul: 7,
        august: 8, aug: 8, september: 9, sep: 9, october: 10, oct: 10,
        november: 11, nov: 11, december: 12, dec: 12
      };
      const toMonth = (v: any) => { const n = Number(v); return (n >= 1 && n <= 12) ? n : mMap[String(v || '').trim().toLowerCase()] ?? null; };
      const toYear = (v: any) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

      const sm = toMonth(filter['Startmonth'] ?? filter['startMonth']);
      const em = toMonth(filter['Endmonth'] ?? filter['endMonth']);
      const sy = toYear(filter['Startyear'] ?? filter['startYear']);
      const ey = toYear(filter['EndYear'] ?? filter['endYear']);

      if (sm != null) this.startMonth = sm;
      if (em != null) this.endMonth = em;
      if (sy != null && ey != null) {
        this.years = [];
        for (let y = sy; y <= ey; y++) this.years.push(y);
        this.startYear = sy;
        this.endYear = ey;
      }
    }

    // Extract metrics from responseList[0].responseData[0]
    const responseData = data?.responseList?.[0]?.responseData?.[0];

    console.log('Chart data structure:', {
      hasResponseList: !!data?.responseList,
      responseListLength: data?.responseList?.length,
      firstResponseData: data?.responseList?.[0]?.responseData?.[0],
      allData: data
    });

    if (responseData && (responseData.total_availability_hrs !== undefined || responseData.unplanned_count !== undefined)) {
      // Extract the metrics from the API response
      const totalAvailabilityHrs = responseData.total_availability_hrs ?? 0;
      const unplannedCount = responseData.unplanned_count ?? 0;
      const plannedCount = responseData.planned_count ?? 0;
      const unavailabilityPercentage = Number(responseData.unavailability_percentage ?? 0);
      const responseAvailabilityCount = Number(responseData.availability_count ?? 0);
      const plannedPercentage = Number(responseData.planned_percentage ?? 0);
      const unplannedPercentage = Number(responseData.unplanned_percentage ?? 0);
      const availabilityPercentage = Number(responseData.availability_percentage ?? 0);
      const totalDowntimePercentage = Number(responseData.total_downtime_percentage ?? 0);
      const availabilityCount = responseAvailabilityCount > 0
        ? responseAvailabilityCount
        : Math.max(totalAvailabilityHrs - unplannedCount - plannedCount, 0);
      console.log('Metrics extracted:', {
        totalAvailabilityHrs,
        unplannedCount,
        plannedCount,
        availabilityCount,
        unavailabilityPercentage,
        plannedPercentage,
        unplannedPercentage,
        availabilityPercentage,
        totalDowntimePercentage
      });

      // Use two stacked rows: hour-based engine uptime and percentage-based downtime profile.
      this.allCategories = ['Engine Uptime Hours', 'Engine Uptime %'];

      this.allSeriesData = {
        cumminsValues: [unplannedCount, unplannedPercentage],
        nonCumminsValues: [plannedCount, plannedPercentage],
        availableValues: [availabilityCount, availabilityPercentage],
        totalValues: [totalAvailabilityHrs, totalDowntimePercentage],
        cumminsLabel: 'Unplanned Downtime',
        nonCumminsLabel: 'Planned Downtime',
        cumminsColor: '#7A942E',
        nonCumminsColor: '#FFA500'
      };

      console.log('Series data set:', this.allSeriesData);

      // Set up chart dimensions
      this.barHeight = this.data?.barHeight || 50;
      this.maxVisibleBars = this.data?.chartConfig?.maxVisibleBars || 5;
      this.sliderIndex = 0;
      this.visibleHeight = this.barHeight * Math.min(this.maxVisibleBars, this.allCategories.length);
      
      // Update visible data
      this.updateVisibleData();
      
      // Resolve widget height and render
      this.resolveWidgetHeight();
      
      try {
        if (this.chartContainer && this.chartContainer.nativeElement) {
          this.chartContainer.nativeElement.style.height = this.getChartHeight();
        }
      } catch (e) {
        console.error('Error setting container height:', e);
      }

      if (this.chartInstance) {
        console.log('Rendering chart with series data');
        this.renderChartWithVisibleData(
          this.allSeriesData.cumminsLabel,
          this.allSeriesData.nonCumminsLabel,
          this.allSeriesData.cumminsColor,
          this.allSeriesData.nonCumminsColor
        );
      } else {
        console.error('ChartInstance not available');
      }
      return;
    }

    console.log('No valid responseData found, falling back to parent');
    // Fallback to parent implementation
    super.initChart(data);
  }

  protected override updateVisibleData(): void {
    if (!this.allSeriesData) return;
    
    // Ensure availableValues exists - calculate as remainder if needed
    if (!this.allSeriesData.availableValues) {
      this.allSeriesData.availableValues = this.allSeriesData.cumminsValues.map((val: number) => 0);
    }
    
    if (this.allCategories.length > this.maxVisibleBars) {
      const end = Math.min(this.sliderIndex + this.maxVisibleBars, this.allCategories.length);
      this.visibleCategories = this.allCategories.slice(this.sliderIndex, end);
      this.visibleSeries = [
        { data: this.allSeriesData.cumminsValues.slice(this.sliderIndex, end) },
        { data: this.allSeriesData.nonCumminsValues.slice(this.sliderIndex, end) },
        { data: this.allSeriesData.availableValues.slice(this.sliderIndex, end) }
      ];
    } else {
      this.visibleCategories = this.allCategories;
      this.visibleSeries = [
        { data: this.allSeriesData.cumminsValues },
        { data: this.allSeriesData.nonCumminsValues },
        { data: this.allSeriesData.availableValues }
      ];
    }
  }

  override previousSlide(): any {
    return super.previousSlide();
  }

  override nextSlide(): any {
    return super.nextSlide();
  }

  override onScrollbarMouseDown(event: any): any {
    return super.onScrollbarMouseDown(event);
  }

  override getScrollbarThumbHeight(): any {
    return super.getScrollbarThumbHeight();
  }

  override getScrollbarThumbTop(): any {
    return super.getScrollbarThumbTop();
  }

  protected override onScrollbarDrag(event: any): any {
    return super.onScrollbarDrag(event);
  }

  protected override onScrollbarMouseUp(): any {
    return super.onScrollbarMouseUp();
  }
  onMonthRangeChange(boundary: 'start' | 'end', event: Event): void {
    const value = Number((event.target as HTMLSelectElement)?.value);
    if (!Number.isFinite(value)) return;
    if (boundary === 'start') {
      this.startMonth = value;
      if (this.startMonth > this.endMonth) {
        this.endMonth = this.startMonth;
      }
    } else {
      this.endMonth = value;
      if (this.endMonth < this.startMonth) {
        this.startMonth = this.endMonth;
      }
    }
    this.applyTimeFilter();
  }

  onYearRangeChange(boundary: 'start' | 'end', event: Event): void {
    const value = Number((event.target as HTMLSelectElement)?.value);
    if (!Number.isFinite(value)) return;
    if (boundary === 'start') {
      this.startYear = value;
      if (this.startYear > this.endYear) {
        this.endYear = this.startYear;
      }
    } else {
      this.endYear = value;
      if (this.endYear < this.startYear) {
        this.startYear = this.endYear;
      }
    }
    this.applyTimeFilter();
  }

  getStartMonthOptions(): Array<{ label: string; value: number }> {
    return this.monthOptions.filter((m) => m.value <= this.endMonth);
  }

  getEndMonthOptions(): Array<{ label: string; value: number }> {
    return this.monthOptions.filter((m) => m.value >= this.startMonth);
  }

  getStartYearOptions(): number[] {
    return this.years.filter((y) => y <= this.endYear);
  }

  getEndYearOptions(): number[] {
    return this.years.filter((y) => y >= this.startYear);
  }

  private applyTimeFilter(): void {
    this.updateGlobalRangeFilters();
    if (!this.allSeriesData || !this.chartInstance) {
      return;
    }
    this.renderChartWithVisibleData(
      this.allSeriesData?.cumminsLabel || 'Unplanned',
      this.allSeriesData?.nonCumminsLabel || 'Planned',
      this.allSeriesData?.cumminsColor || '#7A942E',
      this.allSeriesData?.nonCumminsColor || '#FFA500'
    );
  }

  private updateGlobalRangeFilters(): void {
    const keys = this.resolveRangeFilterKeys();
    const listener =
      this.data?.filterListener || this.data?.filterListner || this.data?.widgetId;

    this.globalFilterService.updateFilterSelection(
      keys.startMonth,
      [this.startMonth],
      false,
      undefined,
      false,
      listener,
    );
    this.globalFilterService.updateFilterSelection(
      keys.endMonth,
      [this.endMonth],
      false,
      undefined,
      false,
      listener,
    );
    this.globalFilterService.updateFilterSelection(
      keys.startYear,
      [this.startYear],
      false,
      undefined,
      false,
      listener,
    );
    this.globalFilterService.updateFilterSelection(
      keys.endYear,
      [this.endYear],
      false,
      undefined,
      true,
      listener,
    );
  }

  private resolveRangeFilterKeys(): { startMonth: string; endMonth: string; startYear: string; endYear: string } {
    const keys = this.data?.chartConfig?.filterKeys || {};
    return {
      startMonth: keys.startMonth || 'startMonth',
      endMonth: keys.endMonth || 'endMonth',
      startYear: keys.startYear || 'startYear',
      endYear: keys.endYear || 'endYear'
    };
  }

  private initializeRangeDefaults(): void {
    const chartConfig = this.data?.chartConfig || {};

    // Read defaults directly from the widget's payload filter (e.g. customPayload.requestData.filter)
    const payloadFilter =
      this.data?.customPayload?.requestData?.filter ||
      this.data?.requestData?.filter ||
      {};

    const payloadStartMonth = this.parseMonthValue(payloadFilter['Startmonth'] ?? payloadFilter['startMonth'] ?? payloadFilter['StartMonth']);
    const payloadEndMonth = this.parseMonthValue(payloadFilter['Endmonth'] ?? payloadFilter['endMonth'] ?? payloadFilter['EndMonth']);
    const payloadStartYear = this.parseYearValue(payloadFilter['Startyear'] ?? payloadFilter['startYear'] ?? payloadFilter['StartYear']);
    const payloadEndYear = this.parseYearValue(payloadFilter['EndYear'] ?? payloadFilter['endYear'] ?? payloadFilter['Endyear']);

    const yearStartFromConfig = Number(chartConfig?.yearRange?.start ?? chartConfig?.startYear ?? chartConfig?.fromYear);
    const yearEndFromConfig = Number(chartConfig?.yearRange?.end ?? chartConfig?.endYear ?? chartConfig?.toYear);

    const monthStartFromConfig = Number(chartConfig?.monthRange?.start ?? chartConfig?.startMonth ?? chartConfig?.fromMonth);
    const monthEndFromConfig = Number(chartConfig?.monthRange?.end ?? chartConfig?.endMonth ?? chartConfig?.toMonth);

    // Build year list: prefer payload years, then chartConfig range, then generic
    if (Number.isFinite(payloadStartYear) && Number.isFinite(payloadEndYear) && payloadStartYear <= payloadEndYear) {
      this.years = [];
      for (let y = payloadStartYear; y <= payloadEndYear; y++) { this.years.push(y); }
    } else if (Number.isFinite(yearStartFromConfig) && Number.isFinite(yearEndFromConfig) && yearStartFromConfig <= yearEndFromConfig) {
      this.years = [];
      for (let y = yearStartFromConfig; y <= yearEndFromConfig; y++) { this.years.push(y); }
    } else {
      this.years = [];
    }

    // Set selected years: prefer payload, then chartConfig, then last in list
    const selectedYearFromConfig = Number(
      chartConfig?.year ??
      chartConfig?.selectedYear ??
      chartConfig?.startYear ??
      chartConfig?.endYear
    );
    if (this.years.length > 0) {
      const defaultYear = this.years[this.years.length - 1];
      const selectedYear = Number.isFinite(selectedYearFromConfig) && this.years.includes(selectedYearFromConfig)
        ? selectedYearFromConfig
        : defaultYear;

      this.startYear = Number.isFinite(payloadStartYear) && this.years.includes(payloadStartYear) ? payloadStartYear : selectedYear;
      this.endYear = Number.isFinite(payloadEndYear) && this.years.includes(payloadEndYear) ? payloadEndYear : selectedYear;
    } else {
      if (Number.isFinite(payloadStartYear)) this.startYear = payloadStartYear;
      if (Number.isFinite(payloadEndYear)) this.endYear = payloadEndYear;
    }

    // Set selected months: prefer payload, then chartConfig, then defaults (1, 12)
    if (Number.isFinite(payloadStartMonth)) {
      this.startMonth = payloadStartMonth;
    } else if (Number.isFinite(monthStartFromConfig) && monthStartFromConfig >= 1 && monthStartFromConfig <= 12) {
      this.startMonth = monthStartFromConfig;
    }

    if (Number.isFinite(payloadEndMonth)) {
      this.endMonth = payloadEndMonth;
    } else if (Number.isFinite(monthEndFromConfig) && monthEndFromConfig >= 1 && monthEndFromConfig <= 12) {
      this.endMonth = monthEndFromConfig;
    }

    if (this.startYear > this.endYear) {
      this.endYear = this.startYear;
    }
    if (this.startMonth > this.endMonth) {
      this.endMonth = this.startMonth;
    }
  }

  private parseMonthValue(value: any): number {
    if (value === undefined || value === null) return NaN;
    const num = Number(value);
    if (Number.isFinite(num) && num >= 1 && num <= 12) return Math.trunc(num);

    const monthMap: Record<string, number> = {
      january: 1, jan: 1,
      february: 2, feb: 2,
      march: 3, mar: 3,
      april: 4, apr: 4,
      may: 5,
      june: 6, jun: 6,
      july: 7, jul: 7,
      august: 8, aug: 8,
      september: 9, sep: 9,
      october: 10, oct: 10,
      november: 11, nov: 11,
      december: 12, dec: 12
    };
    return monthMap[String(value).trim().toLowerCase()] ?? NaN;
  }

  private parseYearValue(value: any): number {
    if (value === undefined || value === null) return NaN;
    const year = Number(value);
    return Number.isFinite(year) ? Math.trunc(year) : NaN;
  }

  private getMonthShortLabel(monthValue: number): string {
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthLabels[monthValue - 1] || String(monthValue);
  }

  private getMonthFullLabel(monthValue: number): string {
    const monthLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return monthLabels[monthValue - 1] || String(monthValue);
  }

  private buildDateFilterPayload(): any {
    return {
      requestData: {
        year: 0
      },
      responseList: [],
      entity: 'mines',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'DateFilter',
          version: '1.0'
        }
      ],
      communicationType: 'rest',
      version: '1.0'
    };
  }

  private fetchDateFilterOptions(): void {
    if (!this.data?.apiUrl || this.dateFilterOptionsLoaded) return;

    const payload = this.buildDateFilterPayload();
    this.configService.getDropdownOptions(this.data.apiUrl, payload).subscribe({
      next: (res: any) => {
        const dateData = res?.responseList?.[0]?.responseData?.[0] || {};

        const backendYears: number[] = Array.isArray(dateData?.years)
          ? dateData.years
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
          : [];

        const backendMonths: number[] = Array.isArray(dateData?.months)
          ? dateData.months
            .map((item: any) => this.parseMonthValue(item))
            .filter((item: number) => Number.isFinite(item))
          : [];

        if (backendYears.length > 0) {
          this.years = Array.from(new Set<number>(backendYears)).sort((a: number, b: number) => a - b);
          if (!this.years.includes(this.startYear)) this.startYear = this.years[0];
          if (!this.years.includes(this.endYear)) this.endYear = this.years[this.years.length - 1];
        }

        if (backendMonths.length > 0) {
          const sortedMonths: number[] = Array.from(new Set<number>(backendMonths)).sort((a: number, b: number) => a - b);
          this.monthOptions = sortedMonths.map((value) => ({
            value,
            label: this.getMonthShortLabel(value)
          }));

          // Use the backend month range as default selection so we always start
          // from the earliest available month, not payload hardcoded month values.
          this.startMonth = this.monthOptions[0].value;
          this.endMonth = this.monthOptions[this.monthOptions.length - 1].value;
        }

        this.dateFilterOptionsLoaded = true;
        this.updateGlobalRangeFilters();
      },
      error: () => {
        // keep existing defaults when DateFilter is unavailable
      }
    });
  }

}


